import { WaypointType } from '@/types/waypoint';

/**
 * Field-label category strings for the tactical drawer header.
 * These are short, uppercase category names that appear above the location name.
 */
export const WAYPOINT_CATEGORY_LABELS: Record<WaypointType, string> = {
  [WaypointType.FoodSource]: 'FOOD SOURCE',
  [WaypointType.Camera]: 'TRAIL CAMERA',
  [WaypointType.WaterSource]: 'WATER SOURCE',
  [WaypointType.GameTrail]: 'GAME TRAIL',
  [WaypointType.BeddingArea]: 'BEDDING AREA',
  [WaypointType.AccessPoint]: 'ACCESS POINT',
  [WaypointType.Blind]: 'STAND',
  [WaypointType.ObservationPoint]: 'OBSERVATION',
  [WaypointType.Other]: 'OTHER',
};

export const WAYPOINT_TYPE_COLORS: Record<WaypointType, string> = {
  [WaypointType.FoodSource]: '#22C55E',        // Green
  [WaypointType.Camera]: '#F59E0B',            // Amber
  [WaypointType.WaterSource]: '#3B82F6',       // Blue
  [WaypointType.GameTrail]: '#D97706',         // Warm amber-brown
  [WaypointType.BeddingArea]: '#A855F7',       // Purple
  [WaypointType.AccessPoint]: '#94A3B8',       // Slate blue-gray
  [WaypointType.Blind]: '#84CC16',             // Lime green
  [WaypointType.ObservationPoint]: '#38BDF8',  // Sky blue
  [WaypointType.Other]: '#9CA3AF',             // Cool gray
};

export const WAYPOINT_TYPE_LABELS: Record<WaypointType, string> = {
  [WaypointType.FoodSource]: 'Food Source',
  [WaypointType.Camera]: 'Trail Camera',
  [WaypointType.WaterSource]: 'Water Source',
  [WaypointType.GameTrail]: 'Game Trail',
  [WaypointType.BeddingArea]: 'Bedding Area',
  [WaypointType.AccessPoint]: 'Access Point',
  [WaypointType.Blind]: 'Blind / Stand',
  [WaypointType.ObservationPoint]: 'Observation Point',
  [WaypointType.Other]: 'Other',
};

export const WAYPOINT_TYPE_DISPLAY_ORDER: WaypointType[] = [
  WaypointType.Blind,
  WaypointType.FoodSource,
  WaypointType.Camera,
  WaypointType.WaterSource,
  WaypointType.GameTrail,
  WaypointType.BeddingArea,
  WaypointType.AccessPoint,
  WaypointType.ObservationPoint,
  WaypointType.Other,
];

// ─── Cluster layer IDs ────────────────────────────────────────────────────────
// Defined here (not in PropertyMap) to avoid non-component exports from a
// component file. Used by PropertyMap and by PublicLandLayer for layer ordering.

export const CLUSTER_SOURCE = 'locations-cluster';
export const CLUSTER_CIRCLE_LAYER = 'cluster-circles';
export const CLUSTER_COUNT_LAYER = 'cluster-counts';

// ─── Game trail paint constants ───────────────────────────────────────────────
// Defined here (not in PropertyMap) to avoid non-component exports from a
// component file (react-refresh lint warnings). Import from here in all consumers.
//
// M-02 (Defect Batch M): polyline color is now resolved at component mount time
// from `--color-ember-500` via getComputedStyle — see PropertyMap.addTrailLayers.
// We intentionally do NOT export a static `GAME_TRAIL_COLOR` constant
// (RULE-055: never introduce hardcoded hex colors; the ember token lives
// in src/index.css and must remain the single source of truth).

export const GAME_TRAIL_WIDTH = 2;
export const GAME_TRAIL_OPACITY = 0.85;

/**
 * Base dash pattern: [dash-length, gap-length]. Tight pattern keeps the
 * "directional flow" cadence visible at typical satellite zoom (z14–z17).
 */
export const GAME_TRAIL_DASHARRAY: [number, number] = [2, 1];

/** Number of animation frames per loop (RULE-153: named visual constant). */
export const GAME_TRAIL_DASH_FRAME_COUNT = 8;

/**
 * Pre-computed [dash, gap] frames stepping the dash phase to create a
 * forward-flow illusion. The dash and gap pair sums to 3 (matching
 * GAME_TRAIL_DASHARRAY total period); fractional steps shift where the
 * "ink-on" portion lands within the cycle, so consecutive frames look like
 * a dash sliding along the line.
 *
 * MapLibre `line-dasharray` accepts numeric arrays; values are multiples of
 * `line-width` (per the GL Style Spec).
 */
export const GAME_TRAIL_DASH_FRAMES: number[][] = Array.from(
  { length: GAME_TRAIL_DASH_FRAME_COUNT },
  (_, i) => {
    // Step from a [2,1] pattern through fractional dash/gap pairs back to [2,1].
    // Each frame keeps a constant period of 3 = 2 (dash) + 1 (gap), but shifts
    // the dash length up by `i / FRAME_COUNT` and the gap by the inverse.
    const t = i / GAME_TRAIL_DASH_FRAME_COUNT;
    const dash = 2 - t;
    const gap = 1 + t;
    return [dash, gap];
  },
);

/** Total animation loop period. */
export const GAME_TRAIL_ANIMATION_PERIOD_MS = 2000;

/** Per-frame interval — derived so FRAME_COUNT * INTERVAL ≈ PERIOD (250ms cadence). */
export const GAME_TRAIL_FRAME_INTERVAL_MS = Math.round(
  GAME_TRAIL_ANIMATION_PERIOD_MS / GAME_TRAIL_DASH_FRAME_COUNT,
);

/**
 * Composite paint object **without color** — color is resolved at runtime
 * from the ember CSS custom property. Consumers must merge `'line-color'`
 * before passing to map.addLayer().
 */
export const GAME_TRAIL_PAINT_BASE = {
  'line-width': GAME_TRAIL_WIDTH,
  'line-dasharray': GAME_TRAIL_DASHARRAY,
  'line-opacity': GAME_TRAIL_OPACITY,
} as const;

/**
 * Resolves the ember-500 color from the current document's CSS custom property.
 * Centralized here so PropertyMap and any mini-map use the same lookup.
 * Returns the trimmed CSS value (e.g. "#c8651a"); throws if called in a
 * non-DOM context.
 */
export function resolveEmberColor(): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue('--color-ember-500')
    .trim();
}
